import React, { useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';

import clsxm from '@/utils/clsxm';

import { RichTypography } from '@/components/atoms/RichTypography';
import { Typography } from '@/components/atoms/Typography';

type Props = {
  isOpen: boolean;
  children?: React.ReactNode | React.ReactNode[];
  maxWidth?: 'fit' | 'lg' | 'xl' | 'xxl' | 'full';
  onCloseClick?: () => void;
  closeOnOutsideClick?: boolean;
  overflowVisible?: boolean;
  title?: string;
  subtitle?: string;
  secondSubtitle?: string;
};

export const BaseModal: React.FC<Props> = ({
  children,
  isOpen,
  closeOnOutsideClick = true,
  title,
  subtitle,
  secondSubtitle,
  maxWidth = 'xl',
  onCloseClick,
  overflowVisible = false
}: Props) => {
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnOutsideClick && onCloseClick) {
        onCloseClick();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [closeOnOutsideClick, onCloseClick]);

  if (!isOpen) {
    return null;
  }

  const handleCloseClick = () => {
    if (closeOnOutsideClick && onCloseClick) {
      onCloseClick();
    }
  };

  return (
    <a onClick={handleCloseClick} className="fixed -top-5 inset-0 bg-primary/30 backdrop-blur-sm flex justify-center items-center z-[1001]">
      <div
        className={clsxm('block bg-white hover:cursor-default h-[80%] border rounded-[30px] backdrop-blur-md relative', {
          'overflow-y-auto': isOpen,
          'w-fit': maxWidth === 'fit',
          'w-[550px]': maxWidth === 'lg',
          'w-[800px]': maxWidth === 'xl',
          'w-[1200px]': maxWidth === 'xxl',
          'w-full': maxWidth === 'full',
          'overflow-visible': overflowVisible
        })}
      >
        {onCloseClick && (
          <div className="sticky top-0 right-0 z-10 w-full p-4 flex justify-end bg-white/80 backdrop-blur-md shadow-sm">
            <button
              className="bg-gray-300 flex justify-center items-center rounded-xl hover:bg-gray-200 w-[32px] h-[32px] cursor-pointer"
              onClick={handleCloseClick}
            >
              <FaTimes className="text-black w-[16px] h-[16px]" />
            </button>
          </div>
        )}

        {title && (
          <div className="w-full px-[40px] py-[30px] border-b border-[#EEF3F9]">
            {title && <Typography variant="heroTitle">{title}</Typography>}
            {subtitle && (
              <Typography variant="h1" color="gray" transform="lowercase">
                {subtitle}
              </Typography>
            )}
          </div>
        )}

        {secondSubtitle && (
          <div className="bg-gray-100 h-[56px] w-full shadow-modal border-t border-gray-300 items-center justify-center flex">
            <RichTypography variant="h3" weight="medium" color="gray" align="center" transColoredClassname="text-black">
              {secondSubtitle}
            </RichTypography>
          </div>
        )}

        <div className="m-auto overflow-auto flex flex-col justify-center">{children}</div>
      </div>
    </a>
  );
};
